import { BulkR2FUploadRow } from '../types/bulkR2fTypes';
import { DEFAULT_WHTS } from 'src/types/WarehouseHandlingTier';

// Standard handling tiers
const standardTiers = DEFAULT_WHTS;

export const parseR2FCSV = (csvText: string): BulkR2FUploadRow[] => {
    const rows = csvText.split('\n');
    const headers = rows[0].split(',').map(h => h.trim());

    // Get header indices
    const headerMap = new Map<string, number>();
    headers.forEach((header, index) => {
        headerMap.set(header.toLowerCase(), index);
    });

    // Validate required headers
    const requiredHeaders = ['warehouseId', 'productionCapability', 'handlingTier', 'currentLowThreshold', 'currentHighThreshold', 'newLowThreshold', 'newHighThreshold'];
    const missingHeaders = requiredHeaders.filter(header => !headerMap.has(header.toLowerCase()));
    
    if (missingHeaders.length > 0) {
        throw new Error(`Invalid CSV format. Missing headers: ${missingHeaders.join(', ')}`);
    }

    // Parse data rows
    return rows.slice(1)
        .filter(row => row.trim()) // Remove empty rows
        .map((row, rowIndex) => {
            const values = row.split(',').map(v => v.trim());
            
            const warehouseId = values[headerMap.get('warehouseid')!];
            const productionCapability = values[headerMap.get('productioncapability')!];
            const handlingTier = values[headerMap.get('handlingtier')!];
            const currentLowThreshold = values[headerMap.get('currentlowthreshold')!];
            const currentHighThreshold = values[headerMap.get('currenthighthreshold')!];
            const newLowThreshold = values[headerMap.get('newlowthreshold')!];
            const newHighThreshold = values[headerMap.get('newhighthreshold')!];

            // Validate handling tier
            if (!standardTiers.includes(handlingTier)) {
                throw new Error(`Invalid handling tier '${handlingTier}' at row ${rowIndex + 2}. Must be one of: ${standardTiers.join(', ')}`);
            }

            // Validate threshold relationship using final values
            const hasNewLow = newLowThreshold && newLowThreshold !== '' && !isNaN(Number(newLowThreshold));
            const hasNewHigh = newHighThreshold && newHighThreshold !== '' && !isNaN(Number(newHighThreshold));
            
            if (hasNewLow || hasNewHigh) {
                const currentLow = currentLowThreshold && !isNaN(Number(currentLowThreshold)) ? Number(currentLowThreshold) : 0;
                const currentHigh = currentHighThreshold && !isNaN(Number(currentHighThreshold)) ? Number(currentHighThreshold) : 0;
                const finalLow = hasNewLow ? Number(newLowThreshold) : currentLow;
                const finalHigh = hasNewHigh ? Number(newHighThreshold) : currentHigh;
                
                if (finalHigh <= finalLow) {
                    throw new Error(`Row ${rowIndex + 2}: High threshold (${finalHigh}) must be greater than low threshold (${finalLow}) for ${warehouseId}/${productionCapability}/${handlingTier}`);
                }
            }

            return {
                warehouseId,
                productionCapability,
                handlingTier,
                currentLowThreshold,
                currentHighThreshold,
                newLowThreshold,
                newHighThreshold
            };
        });
};

export const validateR2FData = (data: BulkR2FUploadRow[]): void => {
    data.forEach((row, index) => {
        // Check if warehouseId, productionCapability, and handlingTier are not empty
        if (!row.warehouseId || !row.productionCapability || !row.handlingTier) {
            throw new Error(`Row ${index + 2}: Missing required fields`);
        }

        // Check if threshold values are valid numbers when present
        if (row.currentLowThreshold && row.currentLowThreshold !== '' && isNaN(Number(row.currentLowThreshold))) {
            throw new Error(`Row ${index + 2}: Invalid current low threshold`);
        }
        if (row.currentHighThreshold && row.currentHighThreshold !== '' && isNaN(Number(row.currentHighThreshold))) {
            throw new Error(`Row ${index + 2}: Invalid current high threshold`);
        }
        if (row.newLowThreshold && row.newLowThreshold !== '' && isNaN(Number(row.newLowThreshold))) {
            throw new Error(`Row ${index + 2}: Invalid new low threshold`);
        }
        if (row.newHighThreshold && row.newHighThreshold !== '' && isNaN(Number(row.newHighThreshold))) {
            throw new Error(`Row ${index + 2}: Invalid new high threshold`);
        }

        // At least one new threshold value should be provided
        const hasNewLowThreshold = row.newLowThreshold && row.newLowThreshold !== '' && !isNaN(Number(row.newLowThreshold));
        const hasNewHighThreshold = row.newHighThreshold && row.newHighThreshold !== '' && !isNaN(Number(row.newHighThreshold));
        
        if (!hasNewLowThreshold && !hasNewHighThreshold) {
            throw new Error(`Row ${index + 2}: At least one new threshold value must be provided`);
        }

        // Determine final threshold values (new values or fallback to current)
        const currentLow = row.currentLowThreshold && !isNaN(Number(row.currentLowThreshold)) ? Number(row.currentLowThreshold) : 0;
        const currentHigh = row.currentHighThreshold && !isNaN(Number(row.currentHighThreshold)) ? Number(row.currentHighThreshold) : 0;
        const finalLow = hasNewLowThreshold ? Number(row.newLowThreshold) : currentLow;
        const finalHigh = hasNewHighThreshold ? Number(row.newHighThreshold) : currentHigh;
        
        // Validate high threshold > low threshold using final values
        if (finalHigh <= finalLow) {
            throw new Error(`Row ${index + 2}: High threshold (${finalHigh}) must be greater than low threshold (${finalLow}) for ${row.warehouseId}/${row.productionCapability}/${row.handlingTier}`);
        }
    });
};

export const exportR2FDataToCSV = (data: any[]): string => {
    // CSV headers matching the upload format
    const headers = [
        'warehouseId',
        'productionCapability', 
        'handlingTier',
        'currentLowThreshold',
        'currentHighThreshold',
        'newLowThreshold',
        'newHighThreshold'
    ];

    // Convert data to CSV rows
    const csvRows: string[] = [];
    csvRows.push(headers.join(','));

    // Process each tier group and warehouse data
    data.forEach(tierGroup => {
        tierGroup.warehouses.forEach((warehouse: any) => {
            warehouse.productionCapabilities.forEach((pc: any) => {
                const row = [
                    warehouse.warehouseId,
                    pc.name,
                    tierGroup.tierName,
                    pc.lowThreshold || '',
                    pc.highThreshold || '',
                    '', // newLowThreshold - empty for export, user fills in
                    ''  // newHighThreshold - empty for export, user fills in
                ];
                csvRows.push(row.join(','));
            });
        });
    });

    return csvRows.join('\n');
};

export const downloadCSV = (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
